import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  TableColumnType,
} from "antd";
import React, { useState } from "react";

import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import CatalogIcon from "../../components/icons/CatalogIcon";
import { ModalAddStationeryProduct } from "../../components/modal/hrm/ModalAddStationeryProduct";
import { ModalImportStationeryProduct } from "../../components/modal/hrm/ModalImportStationeryProduct";
import {
  IQueryStationeryCategory,
  IQueryStationeryProduct,
  IStationeryCategory,
  IStationeryProduct,
} from "../../models/Hrm";
import {
  useDeleteStationeryProduct,
  useGetAllStationeryProduct,
  useGetCategories,
} from "../../query/HrmQuery";
import { notify } from "../../utils/toast";
import { AxiosError } from "axios";

const HrmStationeryProduct = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [editingCatalog, setEditingCatalog] =
    useState<IStationeryProduct | null>(null);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<IQueryStationeryProduct>({
    page: 1,
    limit: 10,
  });
  const [categoryFilters, setCategoryFilters] =
    useState<IQueryStationeryCategory>({
      page: 1,
      limit: 10,
    });

  const { data, isLoading } = useGetAllStationeryProduct(filters);
  const deleteMutation = useDeleteStationeryProduct();
  const [allCategories, setAllCategories] = React.useState<
    IStationeryCategory[]
  >([]);
  const {
    data: categories,
    isLoading: categoriesLoading,
    isFetching,
  } = useGetCategories(categoryFilters);

  React.useEffect(() => {
    if (categories?.data) {
      if (categoryFilters.page === 1) {
        setAllCategories(categories.data);
      } else {
        setAllCategories((prev) => [
          ...prev,
          ...categories.data.filter((c) => !prev.some((p) => p.id === c.id)),
        ]);
      }
    }
  }, [categories, categoryFilters]);

  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: keyof IQueryStationeryProduct, value: string) => {
        setFilters((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [],
  );

  const debouncedCategorySearch = React.useMemo(
    () =>
      debounce((value: string) => {
        setCategoryFilters((prev) => ({
          ...prev,
          name: value || undefined,
          page: 1,
        }));
      }, 300),
    [],
  );

  const showModal = (catalog: IStationeryProduct | null = null) => {
    setEditingCatalog(catalog);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingCatalog(null);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync(id);
      notify.success("Xóa thành công!");
    } catch (error: unknown) {
      const msg = error as AxiosError<{ message: string }>;
      notify.error(msg.response?.data.message || "Xóa thất bại!");
    }
  };

  const columns: TableColumnType<IStationeryProduct>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: filters.name ? `Tên vật phẩm (${filters.name})` : "Tên vật phẩm",
      dataIndex: "name",
      key: "name",
      width: 250,
      ellipsis: true,
      filterDropdown: () => (
        <Input
          placeholder="Tìm theo tên"
          onChange={(e) => debouncedSearch("name", e.target.value)}
          prefix={<SearchOutlined />}
          style={{ padding: 8 }}
        />
      ),
    },

    {
      title: filters.specification
        ? `Quy cách (${filters.specification})`
        : "Quy cách",
      dataIndex: "specification",
      key: "specification",
      width: 150,
      filterDropdown: () => (
        <Input
          placeholder="Tìm theo quy cách"
          onChange={(e) => debouncedSearch("specification", e.target.value)}
          prefix={<SearchOutlined />}
          style={{ padding: 8 }}
        />
      ),
    },
    {
      title: filters.label ? `Nhãn (${filters.label})` : "Nhãn",
      dataIndex: "label",
      key: "label",
      width: 150,
      ellipsis: true,
      filterDropdown: () => (
        <Input
          placeholder="Tìm theo nhãn"
          onChange={(e) => debouncedSearch("label", e.target.value)}
          prefix={<SearchOutlined />}
          style={{ padding: 8 }}
        />
      ),
    },
    {
      title: filters.categoryName
        ? `Danh mục (${filters.categoryName})`
        : "Danh mục",
      dataIndex: ["category", "name"],
      key: "category",
      width: 250,
      ellipsis: true,
      render: (text: string) => (
        <div
          style={{
            maxWidth: 230,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={text}
        >
          {text}
        </div>
      ),
      filterDropdown: () => (
        <Select
          placeholder="Chọn danh mục"
          showSearch
          allowClear
          optionFilterProp="children"
          filterOption={false}
          onSearch={debouncedCategorySearch}
          onChange={(value) => {
            debouncedSearch("categoryName", value);
          }}
          loading={categoriesLoading}
          value={filters.categoryName}
          onPopupScroll={(e) => {
            const target = e.target as HTMLDivElement;
            if (
              !isFetching &&
              target.scrollTop + target.offsetHeight >= target.scrollHeight - 10
            ) {
              if (
                categories?.pagination &&
                (categoryFilters.page || 1) * (categoryFilters.limit || 10) <
                  categories.pagination.total
              ) {
                setCategoryFilters((prev) => ({
                  ...prev,
                  page: (prev.page || 1) + 1,
                }));
              }
            }
          }}
        >
          {allCategories.map((cate) => (
            <Select.Option key={cate.id} value={cate.name}>
              {cate.name}
            </Select.Option>
          ))}
        </Select>
      ),
    },
    { title: "Đơn vị", dataIndex: "unit", key: "unit", width: 100 },
    {
      title: "Giá (Chưa VAT)",
      dataIndex: "price",
      width: 150,
      key: "price",
      render: (record: any) => {
        return record
          ? (Number(record) * 1000).toLocaleString("vi-VN") + " VND"
          : "0.000 VND";
      },
    },
    {
      title: "Thao tác",
      key: "action",
      align: "center" as const,
      width: 120,
      render: (_: any, record: IStationeryProduct) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)} />
          <Popconfirm
            title="Bạn có chắc muốn xóa?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Flex className="p-4" vertical gap={10}>
      <Flex align="center" justify="space-between" gap="small" wrap="wrap">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          Quay lại
        </Button>
        <Space wrap>
          <Button
            type="default"
            icon={<UploadOutlined />}
            onClick={() => setIsImportModalVisible(true)}
          >
            Import Excel
          </Button>
          <Button
            variant="solid"
            color="geekblue"
            icon={<CatalogIcon />}
            onClick={() => navigate("/hrm/stationerys/categories")}
          >
            Danh mục
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
          >
            Thêm mới
          </Button>
        </Space>
      </Flex>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.data}
        loading={isLoading}
        pagination={{
          current: data?.pagination?.page,
          pageSize: data?.pagination?.limit,
          total: data?.pagination?.total,
          onChange: (page, pageSize) => {
            setFilters((prev) => ({ ...prev, page, limit: pageSize }));
          },
        }}
        scroll={{ x: "1000px" }}
      />

      <ModalAddStationeryProduct
        isOpen={isModalVisible}
        onCancel={handleCancel}
        editingCatalog={editingCatalog}
      />
      <ModalImportStationeryProduct
        isOpen={isImportModalVisible}
        onCancel={() => setIsImportModalVisible(false)}
      />
    </Flex>
  );
};

export default HrmStationeryProduct;
